import React, { useMemo, useRef, useState } from "react";
import { Rnd } from "react-rnd";
import classNames from "classnames";
import classes from "./TicketConstructor.module.scss";
import { usePostTerminalTicketPicture } from "../../../hooks/Admin/TerminalAppearance/usePostTerminalTicketPicture";
import { normalizeMediaUrl } from "../../../utils/mediaBackground";
import type {
  TerminalTicketPictureMedia,
  TerminalTicketSettings,
  TicketLayoutAlign,
  TicketLayoutElement,
  TicketLayoutElementType,
} from "../../../types/Admin/TerminalAppearanceTypes";
import {
  TICKET_FIELD_LABELS,
  TICKET_PAPER_MIN_HEIGHT_PX,
  TICKET_PAPER_WIDTH_PX,
  clampTicketPaperHeightPx,
  createTicketLayoutElement,
  ensureTicketLayout,
  getTicketElementPreviewText,
  getTicketLayoutBottomPx,
  isTicketTextFieldType,
  isUniqueTicketFieldType,
  normalizeTicketAlign,
  percentToPx,
  pxToPercent,
  remapTicketLayoutToPaperHeight,
  resolveTicketPaperHeightPx,
  syncLegacyTicketFields,
  ticketFontSizeToPx,
} from "../../../utils/Admin/TicketConstructor/ticketLayout";

type TicketConstructorProps = {
  ticket: TerminalTicketSettings;
  pictures: TerminalTicketPictureMedia[];
  queueName?: string | null;
  disabled?: boolean;
  onChange: (ticket: TerminalTicketSettings) => void;
  onPictureUploaded?: (picture: TerminalTicketPictureMedia) => void;
};

const PALETTE_TYPES: TicketLayoutElementType[] = [
  "COMPANY_NAME",
  "TICKET_NUMBER",
  "TOPIC_NAME",
  "FIRST_LINE",
  "SECOND_LINE",
  "CUSTOM_TEXT",
  "REGISTRATION_DATE",
  "REGISTRATION_TIME",
  "REGISTRATION_DATETIME",
  "IMAGE",
];

const TICKET_IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

const TicketConstructor = ({
  ticket,
  pictures,
  queueName,
  disabled = false,
  onChange,
  onPictureUploaded,
}: TicketConstructorProps) => {
  const layout = useMemo(() => ensureTicketLayout(ticket), [ticket]);
  const [selectedId, setSelectedId] = useState<string | null>(layout[0]?.id ?? null);
  const selected = layout.find((el) => el.id === selectedId) ?? null;
  const committedPaperHeightPx = resolveTicketPaperHeightPx(ticket);
  const [draftPaperHeightPx, setDraftPaperHeightPx] = useState<number | null>(null);
  const [resizeOriginHeightPx, setResizeOriginHeightPx] = useState<number | null>(null);
  const paperHeightPx = draftPaperHeightPx ?? committedPaperHeightPx;
  const resizeStateRef = useRef<{
    startY: number;
    startHeight: number;
    minHeight: number;
  } | null>(null);
  const layoutSpaceHeightPx = resizeOriginHeightPx ?? paperHeightPx;
  const { uploadTerminalTicketPicture, isUploadTerminalTicketPictureLoading } =
    usePostTerminalTicketPicture();

  const commitLayout = (nextLayout: TicketLayoutElement[], nextSelectedId?: string | null) => {
    onChange(
      syncLegacyTicketFields(
        {
          ...ticket,
          paperHeightPx: committedPaperHeightPx,
        },
        nextLayout,
      ),
    );
    if (nextSelectedId !== undefined) {
      setSelectedId(nextSelectedId);
    }
  };

  const updateElement = (id: string, patch: Partial<TicketLayoutElement>) => {
    commitLayout(
      layout.map((el) => (el.id === id ? { ...el, ...patch } : el)),
    );
  };

  const handleAdd = (type: TicketLayoutElementType) => {
    if (disabled) return;
    if (isUniqueTicketFieldType(type) && layout.some((el) => el.type === type)) {
      const existing = layout.find((el) => el.type === type);
      setSelectedId(existing?.id ?? null);
      return;
    }
    const extra: Partial<TicketLayoutElement> = {};
    if (type === "IMAGE" && pictures.length === 1) {
      extra.pictureId = pictures[0].id;
    }
    const next = createTicketLayoutElement(type, layout, extra);
    commitLayout([...layout, next], next.id);
  };

  const handleDelete = (id: string) => {
    if (disabled) return;
    const next = layout.filter((el) => el.id !== id);
    commitLayout(next, next[0]?.id ?? null);
  };

  const heightFromPointer = (clientY: number, state: { startY: number; startHeight: number; minHeight: number }) =>
    clampTicketPaperHeightPx(state.startHeight + (clientY - state.startY), state.minHeight);

  const handlePaperResizePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const startHeight = committedPaperHeightPx;
    resizeStateRef.current = {
      startY: event.clientY,
      startHeight,
      minHeight: Math.max(
        getTicketLayoutBottomPx(layout, startHeight),
        TICKET_PAPER_MIN_HEIGHT_PX,
      ),
    };
    setDraftPaperHeightPx(startHeight);
    setResizeOriginHeightPx(startHeight);
  };

  const handlePaperResizePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = resizeStateRef.current;
    if (!state) return;
    setDraftPaperHeightPx(heightFromPointer(event.clientY, state));
  };

  const finishPaperResize = (clientY: number) => {
    const state = resizeStateRef.current;
    if (!state) return;
    resizeStateRef.current = null;
    const nextHeight = heightFromPointer(clientY, state);
    setDraftPaperHeightPx(null);
    setResizeOriginHeightPx(null);
    if (nextHeight === state.startHeight) return;
    const remapped = remapTicketLayoutToPaperHeight(layout, state.startHeight, nextHeight);
    onChange(
      syncLegacyTicketFields(
        {
          ...ticket,
          paperHeightPx: nextHeight,
        },
        remapped,
      ),
    );
  };

  const handlePaperResizePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!resizeStateRef.current) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    finishPaperResize(event.clientY);
  };

  const handleUploadImage = (
    event: React.ChangeEvent<HTMLInputElement>,
    elementId: string,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (disabled || !file) return;
    uploadTerminalTicketPicture(file, {
      onSuccess: (response) => {
        const data = response?.data as TerminalTicketPictureMedia | undefined;
        if (data?.id == null) return;
        const picture: TerminalTicketPictureMedia = {
          id: data.id,
          fileName: data.fileName,
          url: normalizeMediaUrl(data.url),
        };
        onPictureUploaded?.(picture);
        updateElement(elementId, { pictureId: picture.id });
      },
    });
  };

  return (
    <div className={classes.wrapper}>
      <p className={classes.queueHint}>
        {queueName
          ? `Шаблон талона для очереди «${queueName}». Сохраните настройки, чтобы применить к выбранным очередям.`
          : "Выберите очередь в фильтре, чтобы редактировать шаблон конкретной очереди."}
      </p>
      <div className={classes.layout}>
        <div className={classes.panel}>
          <h4 className={classes.panelTitle}>Поля</h4>
          <div className={classes.paletteList}>
            {PALETTE_TYPES.map((type) => {
              const alreadyAdded =
                isUniqueTicketFieldType(type) && layout.some((el) => el.type === type);
              return (
                <button
                  key={type}
                  type="button"
                  className={classes.paletteButton}
                  disabled={disabled || alreadyAdded}
                  onClick={() => handleAdd(type)}
                >
                  {alreadyAdded ? `${TICKET_FIELD_LABELS[type]} (добавлено)` : TICKET_FIELD_LABELS[type]}
                </button>
              );
            })}
          </div>
        </div>

        <div className={classes.previewWrap}>
          <div
            className={classNames(classes.paperStage, {
              [classes.paperStageResizing]: draftPaperHeightPx != null,
            })}
          >
            <div
              className={classes.paper}
              style={{
                width: TICKET_PAPER_WIDTH_PX,
                height: paperHeightPx,
              }}
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  setSelectedId(null);
                }
              }}
            >
            {layout.map((element) => {
              const isSelected = element.id === selectedId;
              const picture = pictures.find((item) => item.id === element.pictureId);
              return (
                <Rnd
                  key={`${element.type}-${element.id}`}
                  bounds="parent"
                  disableDragging={disabled || draftPaperHeightPx != null}
                  enableResizing={!disabled && draftPaperHeightPx == null}
                  size={{
                    width: percentToPx(element.width, TICKET_PAPER_WIDTH_PX),
                    height: percentToPx(element.height, layoutSpaceHeightPx),
                  }}
                  position={{
                    x: percentToPx(element.x, TICKET_PAPER_WIDTH_PX),
                    y: percentToPx(element.y, layoutSpaceHeightPx),
                  }}
                  minWidth={40}
                  minHeight={20}
                  onDragStart={() => setSelectedId(element.id)}
                  onDragStop={(_, data) => {
                    updateElement(element.id, {
                      x: pxToPercent(data.x, TICKET_PAPER_WIDTH_PX),
                      y: pxToPercent(data.y, layoutSpaceHeightPx),
                    });
                  }}
                  onResizeStop={(_, __, ref, ___, position) => {
                    updateElement(element.id, {
                      x: pxToPercent(position.x, TICKET_PAPER_WIDTH_PX),
                      y: pxToPercent(position.y, layoutSpaceHeightPx),
                      width: pxToPercent(ref.offsetWidth, TICKET_PAPER_WIDTH_PX),
                      height: pxToPercent(ref.offsetHeight, layoutSpaceHeightPx),
                    });
                  }}
                  className={classNames(classes.element, {
                    [classes.elementSelected]: isSelected,
                  })}
                >
                  {element.type === "IMAGE" ? (
                    picture ? (
                      <img
                        src={picture.url}
                        alt={picture.fileName}
                        className={classes.elementImage}
                        draggable={false}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedId(element.id);
                        }}
                      />
                    ) : (
                      <div
                        className={classes.imagePlaceholder}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedId(element.id);
                        }}
                      >
                        Картинка
                      </div>
                    )
                  ) : (
                    <div
                      className={classes.elementContent}
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedId(element.id);
                      }}
                      style={{
                        fontSize: ticketFontSizeToPx(element.fontSize),
                        fontWeight:
                          element.type === "TICKET_NUMBER" ||
                          element.type === "COMPANY_NAME" ||
                          element.type === "TOPIC_NAME"
                            ? 700
                            : 400,
                        justifyContent:
                          normalizeTicketAlign(element.align) === "left"
                            ? "flex-start"
                            : normalizeTicketAlign(element.align) === "right"
                              ? "flex-end"
                              : "center",
                        textAlign: normalizeTicketAlign(element.align),
                      }}
                    >
                      {getTicketElementPreviewText(element)}
                    </div>
                  )}
                </Rnd>
              );
            })}
          </div>
          {!disabled && (
            <div
              className={classes.resizeHandle}
              role="separator"
              aria-orientation="horizontal"
              aria-label="Изменить высоту талона"
              onPointerDown={handlePaperResizePointerDown}
              onPointerMove={handlePaperResizePointerMove}
              onPointerUp={handlePaperResizePointerUp}
              onPointerCancel={handlePaperResizePointerUp}
            />
          )}
          <p className={classes.sizeLabel}>
            {TICKET_PAPER_WIDTH_PX} × {paperHeightPx} px
          </p>
          </div>
        </div>

        <div className={classes.panel}>
          <h4 className={classes.panelTitle}>Свойства</h4>
          {selected ? (
            <div className={classes.properties}>
              <div>
                <strong>{TICKET_FIELD_LABELS[selected.type]}</strong>
              </div>
              {isTicketTextFieldType(selected.type) && (
                <label>
                  Текст
                  <textarea
                    value={selected.text ?? ""}
                    disabled={disabled}
                    onChange={(event) =>
                      updateElement(selected.id, { text: event.target.value })
                    }
                  />
                </label>
              )}
              {selected.type !== "IMAGE" && (
                <label>
                  Размер шрифта (1–8)
                  <input
                    type="number"
                    min={1}
                    max={8}
                    disabled={disabled}
                    value={selected.fontSize ?? 1}
                    onChange={(event) =>
                      updateElement(selected.id, {
                        fontSize: Math.min(
                          8,
                          Math.max(1, Number(event.target.value) || 1),
                        ),
                      })
                    }
                  />
                </label>
              )}
              <label>
                Выравнивание
                <select
                  disabled={disabled}
                  value={normalizeTicketAlign(selected.align)}
                  onChange={(event) =>
                    updateElement(selected.id, {
                      align: event.target.value as TicketLayoutAlign,
                    })
                  }
                >
                  <option value="left">Слева</option>
                  <option value="center">По центру</option>
                  <option value="right">Справа</option>
                </select>
              </label>
              {selected.type === "IMAGE" && (
                <>
                  <label>
                    Изображение
                    <select
                      disabled={disabled || pictures.length === 0}
                      value={selected.pictureId ?? ""}
                      onChange={(event) =>
                        updateElement(selected.id, {
                          pictureId: event.target.value
                            ? Number(event.target.value)
                            : null,
                        })
                      }
                    >
                      <option value="">Не выбрано</option>
                      {pictures.map((picture) => (
                        <option key={picture.id} value={picture.id}>
                          {picture.fileName}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Загрузить файл
                    <input
                      type="file"
                      accept={TICKET_IMAGE_ACCEPT}
                      disabled={disabled || isUploadTerminalTicketPictureLoading}
                      onChange={(event) => handleUploadImage(event, selected.id)}
                    />
                  </label>
                  {isUploadTerminalTicketPictureLoading && (
                    <p className={classes.uploadHint}>Загрузка...</p>
                  )}
                </>
              )}
              <p className={classes.sizeHint}>
                Позиция: {selected.x.toFixed(0)}%, {selected.y.toFixed(0)}% ·{" "}
                {selected.width.toFixed(0)}%×{selected.height.toFixed(0)}%
              </p>
              <button
                type="button"
                className={classes.deleteButton}
                disabled={disabled}
                onClick={() => handleDelete(selected.id)}
              >
                Удалить поле
              </button>
            </div>
          ) : (
            <p className={classes.emptyProperties}>
              Выберите поле на превью или добавьте новое слева.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketConstructor;
